import { Link } from 'react-router-dom';
import React, { FC, ReactNode } from 'react';
import { GenreEntry, GenresResponse } from '../../../store/api';
import Utils from '../../../shared/utils';
import { useLocation } from 'react-router';
import { LoanFormatState } from '../../../store/loanFormat/state';
import { useTranslation } from 'react-i18next';
import { getGenreId, getSubGenreId } from '../../../shared/common/translationMappers/genreIdMapper';

interface Props {
    genres: GenresResponse;
    selectedGenre: string;
    loanFormat: LoanFormatState;
}

const categories = [
    { key: 'topBuckets', title: 'Top Genres' },
    { key: 'buckets', title: 'Genres' }
];

export const GenreSidebar: FC<Props> = props => {
    const { genres, selectedGenre, loanFormat } = props;

    return (
        <>
            {categories.map(category => (
                <div key={category.key}>
                    <h2>{category.title}</h2>
                    <ul>
                        <GenreList
                            selectedGenre={selectedGenre}
                            genres={genres[category.key]}
                            loanFormat={loanFormat}
                        />
                    </ul>
                </div>
            ))}
        </>
    );
};

const GenreList: FC<{ genres: Array<GenreEntry>; selectedGenre: string; loanFormat: LoanFormatState }> = ({
    genres,
    selectedGenre,
    loanFormat
}) => {
    const shouldDisplaySubGenres = genre =>
        genres.length > 0 &&
        (genre.key === selectedGenre || (selectedGenre && selectedGenre.indexOf(`${genre.key}->`) > -1));

    return (
        <ul>
            {genres?.map(genre => (
                <GenreSidebarItem key={genre?.key} selectedGenre={selectedGenre} genre={genre} loanFormat={loanFormat}>
                    {shouldDisplaySubGenres(genre) ? (
                        <GenreList selectedGenre={selectedGenre} genres={genre.buckets} loanFormat={loanFormat} />
                    ) : null}
                </GenreSidebarItem>
            ))}
        </ul>
    );
};

const GenreSidebarItem: FC<{
    selectedGenre: string;
    genre: GenreEntry;
    loanFormat: LoanFormatState;
    children: ReactNode;
}> = ({ selectedGenre, genre, loanFormat, children }) => {
    const { search } = useLocation();
    const { t } = useTranslation();
    const genreId = getGenreId(genre.key);
    const subGenreId = getSubGenreId(genre.key);
    return (
        <li key={genre.key}>
            <Link
                to={buildLink({ genre: genre.key, search, loanFormat: loanFormat.key })}
                className={genre.key === selectedGenre ? 'active' : ''}
            >
                {t(`filters:genre.${subGenreId ?? genreId}`)}
                <span className="counter"> ({genre.docCount})</span>
            </Link>

            {children}
        </li>
    );
};

function buildLink({ genre, search, loanFormat }: { genre: string; search: string; loanFormat: string }) {
    let subGenre = '';
    if (genre.indexOf('->') > -1) {
        const genreParts = genre.split('->');
        genre = genreParts[0];
        subGenre = genreParts[1];
    }
    const params = new URLSearchParams(search);
    params.delete('page');
    return (
        Utils.buildUrl([
            'collection',
            'by-genre',
            loanFormat.toLowerCase(),
            encodeURIComponent(genre),
            encodeURIComponent(subGenre)
        ]) +
        '?' +
        params.toString()
    );
}
